/**
 * VerlaufRail — unified history rail.
 *
 * Merges rollback-able sessions (GET /sessions) with log-only AP
 * activity entries (GET /ap/activity) into one chronological list.
 * Rollback button only appears for session entries.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  IconChevronDown,
  IconChevronRight,
  IconHistory,
} from "@tabler/icons-react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useCallback, useState } from "react"
import { api } from "../../../api/client"
import { useStore } from "../../../store/useStore"
import type { ScopeListItem, SessionOut } from "../../../types"
import { localeTagFor } from "../../../utils/locale"

const MONITOR_AP_IDS = new Set(["substanz-radar"])

type ActivityEntry = {
  id: string
  ap_id: string
  kind: string
  summary: string
  created_at: string
  source_ref?: { kind?: string; monitor_ap_id?: string } | null
}

type MergedEntry =
  | { type: "session"; data: SessionOut; ts: string }
  | { type: "activity"; data: ActivityEntry; ts: string }

export function VerlaufRail() {
  const [collapsed, setCollapsed] = useState(false)
  const { translate } = useTranslation()

  const { data: sessions = [] } = useQuery({
    queryKey: ["ap-verlauf"],
    queryFn: () => api.sessionList(),
    staleTime: 10_000,
  })

  const { data: activities = [] } = useQuery({
    queryKey: ["ap-activity"],
    queryFn: () => api.apActivityList(50),
    staleTime: 10_000,
  })

  const committedSessions = sessions.filter((s) => s.status === "committed")

  const merged: MergedEntry[] = [
    ...committedSessions.map((s) => ({
      type: "session" as const,
      data: s,
      ts: s.updated_at ?? s.created_at ?? "",
    })),
    ...activities.map((a) => ({
      type: "activity" as const,
      data: a as ActivityEntry,
      ts: a.created_at,
    })),
  ].sort((a, b) => b.ts.localeCompare(a.ts))

  return (
    <div className="flex flex-col border-t border-grey4 bg-grey1">
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="flex w-full items-center gap-1.5 border-b border-grey4 px-4 py-2.5 text-left hover:bg-grey2"
      >
        {collapsed ? (
          <IconChevronRight size={14} className="text-grey5" />
        ) : (
          <IconChevronDown size={14} className="text-grey5" />
        )}
        <IconHistory size={14} className="text-grey5" />
        <span className="text-xs font-semibold uppercase tracking-wide text-grey5">
          {translate("QMS/VERLAUF/TITLE")}
        </span>
        {merged.length > 0 && (
          <span className="ml-auto text-[11px] text-grey5">
            {merged.length}
          </span>
        )}
      </button>

      {!collapsed && (
        <div className="max-h-[40vh] overflow-y-auto px-3 py-2">
          {merged.length === 0 && (
            <p className="py-3 text-center text-xs text-grey5">
              {translate("QMS/VERLAUF/NO_ENTRIES")}
            </p>
          )}
          <ul className="space-y-1.5">
            {merged.map((entry) =>
              entry.type === "session" ? (
                <VerlaufEntry key={`s-${entry.data.id}`} session={entry.data} />
              ) : (
                <ActivityLogEntry
                  key={`a-${entry.data.id}`}
                  activity={entry.data}
                />
              ),
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

function VerlaufEntry({ session }: { session: SessionOut }) {
  const { translate, currentLanguage } = useTranslation()
  const localeTag = localeTagFor(currentLanguage)
  const queryClient = useQueryClient()
  const {
    setDbConfigured,
    setScopes,
    initHiddenScopes,
    setActiveDiffSessionId,
    exitDiffMode,
    clearActiveExport,
    clearSelection,
  } = useStore()
  const [rolling, setRolling] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const file = session.files?.[0]?.name ?? "Import"
  const sourceLabel =
    session.source === "agent"
      ? translate("QMS/VERLAUF/SOURCE_AGENT")
      : translate("QMS/VERLAUF/SOURCE_MANUAL")

  const { recordCount, scopeCount } = summarizeSession(session)
  const summary = translate("QMS/VERLAUF/SETUP_SUMMARY", {
    file,
    scopes: `${scopeCount} ${translate(scopeCount === 1 ? "QMS/CONFIG/PREVIEW_SCOPES_SINGULAR" : "QMS/CONFIG/PREVIEW_SCOPES_PLURAL")} · ${recordCount.toLocaleString(localeTag)} ${translate(recordCount === 1 ? "QMS/VERLAUF/RECORD_SUFFIX_SINGULAR" : "QMS/VERLAUF/RECORD_SUFFIX_PLURAL")}`,
  })

  const handleRollback = useCallback(async () => {
    if (rolling) return
    if (!window.confirm(translate("QMS/VERLAUF/UNDO_CONFIRM", { summary }))) {
      return
    }
    setRolling(true)
    setError(null)
    try {
      await api.sessionRollback(session.id)
      const [freshScopes, counts] = await Promise.all([
        api.listScopes().catch(() => [] as ScopeListItem[]),
        api.scopeRecordCounts().catch(() => ({}) as Record<string, number>),
      ])
      setDbConfigured(freshScopes.length > 0)
      if (freshScopes.length > 0) {
        setScopes(freshScopes)
        const hiddenIds = freshScopes
          .filter((s) => s.is_hidden)
          .map((s) => s.id)
        initHiddenScopes(
          freshScopes.map((s) => s.id),
          counts,
          hiddenIds,
        )
      } else {
        setScopes([])
      }
      setActiveDiffSessionId(null)
      exitDiffMode()
      // Rollback hard-deletes the imported records; drop stale selected/active
      // export ids so conformity/inquiry views don't query deleted records.
      clearActiveExport()
      clearSelection()
      await queryClient.invalidateQueries()
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : translate("QMS/VERLAUF/UNDO_FAILED"),
      )
    } finally {
      setRolling(false)
    }
  }, [
    rolling,
    summary,
    session.id,
    queryClient,
    setDbConfigured,
    setScopes,
    initHiddenScopes,
    setActiveDiffSessionId,
    exitDiffMode,
    clearActiveExport,
    clearSelection,
  ])

  return (
    <li className="rounded border border-grey4/60 bg-grey2/60 px-3 py-2 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-medium text-white" title={summary}>
          {file}
        </span>
        <span
          className={clsx(
            "shrink-0 rounded px-1.5 py-0.5 text-[10px]",
            session.source === "agent"
              ? "bg-yellow5/20 text-yellow1"
              : "bg-grey3 text-grey5",
          )}
        >
          {sourceLabel}
        </span>
      </div>
      <p className="mt-0.5 truncate text-grey5" title={summary}>
        {scopeCount}{" "}
        {translate(
          scopeCount === 1
            ? "QMS/CONFIG/PREVIEW_SCOPES_SINGULAR"
            : "QMS/CONFIG/PREVIEW_SCOPES_PLURAL",
        )}{" "}
        · {translate("QMS/VERLAUF/RECORDS_COUNT", { count: recordCount })}
      </p>
      <div className="mt-1 flex items-center justify-between gap-2 text-grey5">
        <span className="truncate">
          {formatTimestamp(session.updated_at ?? session.created_at, localeTag)}
        </span>
        <button
          onClick={handleRollback}
          disabled={rolling}
          className="rounded border border-grey4 px-2 py-0.5 text-[11px] text-grey6 hover:bg-grey3 disabled:opacity-40"
        >
          {rolling
            ? translate("QMS/VERLAUF/UNDOING")
            : translate("QMS/VERLAUF/UNDO_LABEL")}
        </button>
      </div>
      {error && (
        <p className="mt-1 rounded border border-red1/30 bg-red1/10 px-2 py-1 text-[11px] text-red1">
          {error}
        </p>
      )}
    </li>
  )
}

function ActivityLogEntry({ activity }: { activity: ActivityEntry }) {
  const { translate, currentLanguage } = useTranslation()
  const localeTag = localeTagFor(currentLanguage)
  const AP_LABELS: Record<string, string> = {
    configuration: translate("QMS/VERLAUF/CONFIGURATION"),
    supplier: translate("QMS/AP/SUPPLIER/NAME"),
    "product-conformity": translate("QMS/VERLAUF/PRODUCT_CONFORMITY"),
    "customer-conformity": translate("QMS/VERLAUF/CUSTOMER_CONFORMITY"),
  }
  const apLabel = AP_LABELS[activity.ap_id] ?? activity.ap_id
  const isMonitorKind =
    activity.kind.startsWith("monitor_") || MONITOR_AP_IDS.has(activity.ap_id)
  const byMonitor = activity.source_ref?.kind === "monitor"
  return (
    <li className="rounded border border-grey4/40 bg-grey1/80 px-3 py-2 text-xs text-grey5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={clsx(
            "rounded px-1.5 py-0.5 text-[10px]",
            isMonitorKind
              ? "bg-yellow1/15 text-yellow1"
              : "bg-grey3 text-grey6",
          )}
        >
          {apLabel}
        </span>
        {byMonitor && (
          <span className="rounded border border-yellow1/20 bg-yellow1/10 px-1.5 py-0.5 text-[10px] text-yellow1/80">
            {translate("QMS/VERLAUF/BY_MONITOR")}
          </span>
        )}
      </div>
      <p className="mt-0.5 truncate" title={activity.summary}>
        {activity.summary}
      </p>
      <p className="mt-0.5 text-[10px]">
        {formatTimestamp(activity.created_at, localeTag)}
      </p>
    </li>
  )
}

function summarizeSession(session: SessionOut): {
  recordCount: number
  scopeCount: number
} {
  const plan = session.plan as {
    scopes_activated?: string[]
    record_ops?: Record<string, { create?: number; update?: number }>
  } | null
  const scopeCount =
    plan?.scopes_activated?.length ??
    Object.keys(plan?.record_ops ?? {}).length ??
    0
  const recordCount = Object.values(plan?.record_ops ?? {}).reduce(
    (sum, ops) => sum + (ops.create ?? 0),
    0,
  )
  return { recordCount, scopeCount }
}

function formatTimestamp(ts: string | undefined, localeTag: string): string {
  if (!ts) return ""
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ts
  return d.toLocaleString(localeTag, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}
